<script lang="ts">
	import Accordion from '$lib/components/controls/Accordion.svelte'
	import { TICKERDATA_METRICS, TICKERDATALIVE_METRICS } from '$lib/constants/attributes'
</script>

<div class="px-6 mb-20 lg:px-8 lg:max-w-screen-xl mx-auto">
	<div class="flex flex-col items-center justify-center my-20">
		<h1 class="text-3xl lg:text-[44px] lg:leading-tight font-bold pb-5">Docs</h1>
		<p>Everything you need to know to get started with Tickerdata.</p>
	</div>
	<div class="flex flex-col gap-6">
		<Accordion>
			{#snippet head()}
						<h2 class="text-3xl font-bold" >Getting Started</h2>
					{/snippet}
			{#snippet details()}
						<ol class="space-y-4 list-decimal list-inside" >
					<li>Sign in/Create an account by clicking the "Get Started" button.</li>
					<li>
						Click on the Google Account you would like to use with Tickerdata.
						<ul class="ps-5 mt-2 space-y-1 list-disc list-inside">
							<li>
								Note, Tickerdata is exclusive to Google Sheets right now. You must have and use the
								same Google Account to download and use the Tickerdata tool.
							</li>
						</ul>
					</li>
					<li>Authorize tickerdata.com to access required Google credentials.</li>
					<li>
						Visit our Tickerdata <a href="/#pricing" class="text-lilac-500 hover:underline">plans.</a>
					</li>
					<li>Complete checkout steps.</li>
				</ol>
					{/snippet}
		</Accordion>
		<Accordion>
			{#snippet head()}
						<h2 class="text-3xl font-bold" >Install Tickerdata</h2>
					{/snippet}
			{#snippet details()}
						<ol class="space-y-4 list-decimal list-inside" >
					<li>Navigate to Google Sheets.</li>
					<li>
						Ensure you are logged into Google account that is linked to your tickerdata.com user
						account.
						<ul class="ps-5 mt-2 space-y-1 list-disc list-inside">
							<li>
								If you have subscribed to tickerdata.com but do not remember which Google Account you
								used, you can find it by visiting the <a
									href="/account"
									class="text-lilac-500 hover:underline">Account Page</a
								>.
							</li>
						</ul>
					</li>
					<li>Once logged into Google Sheets, create or enter a new sheet.</li>
					<li>
						Visit the Extensions tab on the top right of the toolbar > "Add-ons" > "Get add-ons".
						(Alternatively, you can find the app on
						<a
							href="http://workspace.google.com/marketplace/app/appname/99466380521"
							target="_blank"
							rel="noopener noreferrer"
							class="text-lilac-500 hover:underline">Google Marketplace.</a
						>)
					</li>
					<li>Type "Tickerdata" in the Google Add-Ons search bar.</li>
					<li>Install the Add-On called "Tickerdata". Ensure that it is made by "Tickerdata".</li>
					<li>
						Authorize Google to use the app inside your Google Sheet–Rest assured, your privacy and
						security are our utmost priorities.
						<ul class="ps-5 mt-2 space-y-1 list-disc list-inside">
							<li>
								<b
									>It's important to note that in order for the Tickerdata Add-On to efficiently
									deliver stock data to your Sheet, <i
										>Google requires us to have read/write access to your Google Sheets.</i
									> This standard procedure is applicable to all types of Google Add-Ons seamlessly integrated
									within Google Sheets.</b
								> It's essential to emphasize that we handle your data with the utmost care and respect.
								Our sole purpose is to facilitate the retrieval of data to enhance your experience, without
								any use or exploitation of your personal information. Your trust is invaluable to us, and
								we appreciate your understanding as we strive to provide you with the best service possible.
							</li>
						</ul>
					</li>
				</ol>
					{/snippet}
		</Accordion>
		<Accordion>
			{#snippet head()}
						<h2 class="text-3xl font-bold" >Use Tickerdata</h2>
					{/snippet}
			{#snippet details()}
						<ol class="space-y-4 list-decimal list-inside" >
					<li>Open a fresh Google Sheet.</li>
					<li>
						Navigate to the "Extensions" tab at the top right of the toolbar and you should find an
						Add-On called "Tickerdata" installed.
					</li>
					<li>Hover over Tickerdata, and click "Log in".</li>
					<li>
						Once logged-in, you will instantly have access to thousands of data points, real-time and
						historic.
					</li>
					<li>
						To run a search, type
						<code>=tickerdata()</code>
						into a cell.
					</li>
					<li>
						Now, you can input the your desired stock exchange, the stock metric desired, and the year
						e.g. <pre class="inline">("AAPL", "capital expenditure", 2020).</pre>
						<ul class="ps-5 mt-2 space-y-1 list-disc list-inside">
							<li>
								Note, you must surround all text with double quotes (not single) in order for Google
								to not find an error in the function.
							</li>
							<li>
								You can query TTM-based data by using the <code>ttm</code> attribute instead of
								entering the year. Your function should now look like this:
								<pre class="inline">("AAPL", "average payables", "ttm").</pre>
								<ul class="ps-7 mt-2 space-y-1 list-disc list-inside">
									<li class="italic font-bold">
										*Note not all metrics support TTM data. We are working on adding support for more
										metrics!
									</li>
								</ul>
							</li>
						</ul>
					</li>
					<li>
						We recommend making the function easily reusable. Luckily, Google Sheets makes this a
						breeze by allowing you to pass values as cell references (e.g, <pre
							class="inline">(A1, B1, C1)</pre>
					</li>
					<li>
						If your functions aren't loading any data, it's probably an issue with Google Sheets. See
						the "Troubleshooting" section below on this page!
					</li>
				</ol>
					{/snippet}
		</Accordion>
		<Accordion>
			{#snippet head()}
						<h2 class="text-3xl font-bold" >Tickerdata Attributes</h2>
					{/snippet}
			{#snippet details()}
						<div >
					<p>
						Below you will find a list of metrics that are available when using the <code
							>=TICKERDATA()</code
						>
						function. These metrics are used in the "attribute" section of the function.
					</p>
					<div class="pt-10">
						<ul>
							{#each TICKERDATA_METRICS as [title, metrics]}
								<li class="[&:not(:first-child)]:mt-6">
									<Accordion shadow="shadow-lg">
										{#snippet head()}
																		<h2 >{title}</h2>
																	{/snippet}
										{#snippet details()}
																		<ul  class="grid grid-cols-3 justify-items-center">
												{#each metrics as metric}
													<li>{metric}</li>
												{/each}
											</ul>
																	{/snippet}
									</Accordion>
								</li>
							{/each}
						</ul>
					</div>
				</div>
					{/snippet}
		</Accordion>
		<Accordion>
			{#snippet head()}
						<h2 class="text-3xl font-bold" >TickerdataLive Attributes</h2>
					{/snippet}
			{#snippet details()}
						<div >
					<p>
						Below you will find a list of metrics that are available when using the <code
							>=TICKERDATALIVE()</code
						>
						function. These metrics are used in the "attribute" section of the function.
					</p>
					<div class="pt-10">
						<ul class="grid grid-cols-3 justify-items-center">
							{#each TICKERDATALIVE_METRICS as metric}
								<li>{metric}</li>
							{/each}
						</ul>
					</div>
				</div>
					{/snippet}
		</Accordion>
		<Accordion>
			{#snippet head()}
						<h2 class="text-3xl font-bold" >Tickerdata Exchanges</h2>
					{/snippet}
			{#snippet details()}
						<div >
					<p>
						Tickerdata supports over 70+ exchanges from all over the world! To see if we support your
						exchange, visit our <a href="/exchanges" class="text-lilac-500 hover:underline"
							>exchanges page</a
						>!
					</p>
				</div>
					{/snippet}
		</Accordion>
		<Accordion>
			{#snippet head()}
						<h2 class="text-3xl font-bold" >Troubleshooting</h2>
					{/snippet}
			{#snippet details()}
						<ul class="list-decimal list-inside" >
					<li>
						If your functions aren't loading any data, it's probably an issue with Google Sheets. To
						fix this, do the following:

						<ul class="ps-5 mt-2 space-y-1 list-disc list-inside pb-3">
							<li>
								Ensure you have an active membership on <a
									href="https://tickerdata.com"
									class="text-lilac-500 hover:underline">tickerdata.com</a
								>
							</li>
							<li>
								<a
									href="https://filecamp.com/support/problem-solving/hard-refresh/"
									target="_blank"
									rel="noopener norefferer"
									class="text-lilac-500 hover:underline">Hard refresh</a
								>
								your browser

								<ul class="ps-7 mt-2 space-y-1 list-[circle] list-inside">
									<li>
										<span class="italic font-bold">Note!</span> You must ensure that your
										<span class="italic font-bold">entire browser page reloads.</span> Google Sheets in
										particular wants to prevent you from doing this for optimization purposes. However,
										if you don't properly hard-refresh, your Google Sheets might stay out of sync.
									</li>
								</ul>
							</li>
							<li>Close all tabs and quit your browser</li>
							<li>
								Reopen the Google Sheet you were using with the function. Ensure that this tab simply
								isn't "restored" when you reopen your browser. You should see the entire page load
								upon opening.
							</li>
							<li>
								After completing these steps, you should follow the steps to log in to the tool and
								refresh your functions.
							</li>
						</ul>
					</li>
					<li class="bg-red-100 rounded p-4">
						We've noticed that Google has a bug when copying Google Sheets that contain an Add-on. The
						script essentially doesn't copy over correctly. An example of this would be on the
						"Tickerdata DIY Toolkit". If this happens to you, please attempt the following:
						<ul class="ps-5 mt-2 space-y-1 list-disc list-inside pb-3">
							<li>
								In the "Tickerdata DIY Toolkit", in the header navigation, go to "Extensions" >
								"Add-ons" > "Manage add-ons"
							</li>
							<li>Find the Tickerdata app and click Uninstall</li>
							<li>
								Reinstall the Tickerdata app by searching for "Tickerdata" and clicking "Individual
								Install"
							</li>
							<li>
								Navigate to "Extensions" > "Tickerdata" > "Log in". After this, click "Refresh" you
								can click "Refresh" to refresh all of the functions.
							</li>
						</ul>
					</li>
				</ul>
					{/snippet}
		</Accordion>
	</div>
</div>
